import { memo, useCallback, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop/types";
import { getCroppedImg } from "../../utils/getCroppedImg";

interface Props {
  show: boolean;
  imageSrc: string;
  onClose: () => void;
  onSave: (file: File, base64: string) => void;
  aspectRatio?: number;
}

const DEFAULT_ASPECT_RATIO = 1;
const CROPPER_HEIGHT = 400;

const CROPPER_CONTAINER_STYLE = {
  position: "relative" as const,
  width: "100%",
  height: CROPPER_HEIGHT,
  background: "#333",
} as const;

const MODAL_BODY_STYLE = {
  minHeight: CROPPER_HEIGHT,
} as const;

function ImageCropperModal({
  show,
  imageSrc,
  onClose,
  onSave,
  aspectRatio = DEFAULT_ASPECT_RATIO,
}: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, "blob");
      const file = new File([blob], "cropped.jpg", { type: "image/png" });
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        onSave(file, base64);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Cropping failed", err);
      setUploading(false);
    }
  }, [imageSrc, croppedAreaPixels, onSave]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Crop Image</Modal.Title>
      </Modal.Header>
      <Modal.Body style={MODAL_BODY_STYLE}>
        <div style={CROPPER_CONTAINER_STYLE}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape="round"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleSave} disabled={uploading}>
          {uploading ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ImageCropperModal.displayName = "ImageCropperModal";

export default memo(ImageCropperModal);
